import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { PlusIcon, CheckBadgeIcon } from '@heroicons/react/24/outline';
import { Trash2, Edit2, Save, X } from 'lucide-react';
import toast from 'react-hot-toast';
import ConfirmDialog from '../components/ConfirmDialog';
import * as HeroIconsOutline from '@heroicons/react/24/outline';

const AdminScorecardEligibilityCardsPage = () => {
    const [cards, setCards] = useState([]);
    const [settings, setSettings] = useState({
        section_title: 'Home Loan Eligibility Calculator Based on Salary',
        section_subtitle: 'Your monthly income is the single biggest factor...'
    });
    const [loading, setLoading] = useState(true);
    const [editingCard, setEditingCard] = useState(null);
    const [isAdding, setIsAdding] = useState(false);
    const [savingSettings, setSavingSettings] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, cardId: null });

    const [formData, setFormData] = useState({
        icon_name: 'BuildingOfficeIcon',
        title_en: '',
        color_class: 'text-blue-600',
        bg_class: 'bg-blue-50',
        points_en: [''],
        order_index: 0,
        is_active: true
    });

    const iconsList = ['BuildingOfficeIcon', 'ChartBarIcon', 'GlobeAltIcon', 'HomeIcon', 'BriefcaseIcon', 'UserIcon', 'BanknotesIcon'];
    const colorsList = [
        { name: 'Blue', color: 'text-blue-600', bg: 'bg-blue-50' },
        { name: 'Green', color: 'text-green-600', bg: 'bg-green-50' },
        { name: 'Purple', color: 'text-purple-600', bg: 'bg-purple-50' },
        { name: 'Orange', color: 'text-orange-600', bg: 'bg-orange-50' },
        { name: 'Red', color: 'text-red-600', bg: 'bg-red-50' },
    ];

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [cardsRes, settingsRes] = await Promise.all([
                axios.get('/api/admin/eligibility-scorecard/cards'),
                axios.get('/api/admin/eligibility-scorecard/settings?page_type=eligibility_cards_section')
            ]);
            
            if (cardsRes.data.success) {
                setCards(cardsRes.data.data);
            }
            if (settingsRes.data.success && settingsRes.data.data) {
                setSettings({
                    section_title: settingsRes.data.data.section_title || '',
                    section_subtitle: settingsRes.data.data.section_subtitle || ''
                });
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load eligibility data');
        } finally {
            setLoading(false);
        }
    };

    const handleSettingsSave = async () => {
        try {
            setSavingSettings(true);
            await axios.put('/api/admin/eligibility-scorecard/settings', {
                page_type: 'eligibility_cards_section',
                section_title: settings.section_title,
                section_subtitle: settings.section_subtitle
            });
            toast.success('Settings updated successfully');
        } catch (error) {
            console.error('Error saving settings:', error);
            toast.error('Failed to update settings');
        } finally {
            setSavingSettings(false);
        }
    };

    const handleAddPoint = () => {
        setFormData(prev => ({
            ...prev,
            points_en: [...prev.points_en, '']
        }));
    };

    const handleRemovePoint = (index) => {
        setFormData(prev => ({
            ...prev,
            points_en: prev.points_en.filter((_, i) => i !== index)
        }));
    };

    const handlePointChange = (index, value) => {
        setFormData(prev => {
            const newPoints = [...prev.points_en];
            newPoints[index] = value;
            return { ...prev, points_en: newPoints };
        });
    };

    const handleEdit = (card) => {
        setEditingCard(card);
        setIsAdding(false);
        setFormData({
            icon_name: card.icon_name,
            title_en: card.title_en,
            color_class: card.color_class,
            bg_class: card.bg_class,
            points_en: typeof card.points_en === 'string' ? JSON.parse(card.points_en) : card.points_en,
            order_index: card.order_index,
            is_active: card.is_active !== undefined ? card.is_active : true
        });
    };

    const handleCancel = () => {
        setEditingCard(null);
        setIsAdding(false);
        setFormData({
            icon_name: 'BuildingOfficeIcon',
            title_en: '',
            color_class: 'text-blue-600',
            bg_class: 'bg-blue-50',
            points_en: [''],
            order_index: 0,
            is_active: true
        });
    };

    const handleSaveCard = async () => {
        try {
            // Keep existing Tamil data if editing, or default to empty if adding
            const dataToSave = {
                ...formData,
                title_ta: editingCard ? editingCard.title_ta : formData.title_en,
                points_ta: editingCard ? (typeof editingCard.points_ta === 'string' ? JSON.parse(editingCard.points_ta) : editingCard.points_ta) : formData.points_en
            };

            if (isAdding) {
                const response = await axios.post('/api/admin/eligibility-scorecard/cards', dataToSave);
                if (response.data.success) {
                    toast.success('Card added successfully');
                    fetchData();
                    handleCancel();
                }
            } else {
                const response = await axios.put(`/api/admin/eligibility-scorecard/cards/${editingCard.id}`, dataToSave);
                if (response.data.success) {
                    toast.success('Card updated successfully');
                    fetchData();
                    handleCancel();
                }
            }
        } catch (error) {
            console.error('Error saving card:', error);
            toast.error('Failed to save card');
        }
    };

    const confirmDelete = async () => {
        try {
            const response = await axios.delete(`/api/admin/eligibility-scorecard/cards/${deleteConfirm.cardId}`);
            if (response.data.success) {
                toast.success('Card deleted successfully');
                fetchData();
                setDeleteConfirm({ isOpen: false, cardId: null });
            }
        } catch (error) {
            console.error('Error deleting card:', error);
            toast.error('Failed to delete card');
        }
    };

    const renderCardGrid = () => {
        if (loading) {
            return (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="bg-white rounded-2xl h-48 shadow-sm animate-pulse border border-slate-100"></div>
                    ))}
                </div>
            );
        }

        if (cards.length === 0) {
            return (
                <div className="bg-white rounded-3xl p-12 text-center shadow-sm border border-slate-100">
                    <div className="max-w-md mx-auto">
                        <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                            <CheckBadgeIcon className="h-8 w-8 text-slate-400" />
                        </div>
                        <h3 className="text-xl font-bold text-slate-800 mb-2">No cards added yet</h3>
                        <p className="text-slate-500 mb-6">Create eligibility cards to showcase on the Scorecard page.</p>
                        <button
                            onClick={() => setIsAdding(true)}
                            className="bg-[#283079] hover:bg-[#1f2559] text-white px-6 py-3 rounded-xl font-semibold transition-all shadow-lg"
                        >
                            Add First Card
                        </button>
                    </div>
                </div>
            );
        }

        return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {cards.map((card) => {
                    const OutlineIcon = HeroIconsOutline[card.icon_name];
                    const IconComponent = OutlineIcon || HeroIconsOutline.StarIcon;
                    const points = typeof card.points_en === 'string' ? JSON.parse(card.points_en) : card.points_en;

                    return (
                        <div key={card.id} className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden hover:shadow-md transition-all group relative flex flex-col h-full">
                            <div className="absolute top-4 right-4 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity z-10 bg-white/90 backdrop-blur rounded-lg shadow-sm p-1">
                                <button
                                    onClick={() => handleEdit(card)}
                                    className="p-1.5 hover:bg-slate-100 rounded text-slate-500 hover:text-[#283079] transition-colors"
                                    title="Edit"
                                >
                                    <Edit2 size={16} />
                                </button>
                                <button
                                    onClick={() => setDeleteConfirm({ isOpen: true, cardId: card.id })}
                                    className="p-1.5 hover:bg-slate-100 rounded text-slate-500 hover:text-red-600 transition-colors"
                                    title="Delete"
                                >
                                    <Trash2 size={16} />
                                </button>
                            </div>
                            
                            <div className="p-6 flex-grow flex flex-col">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-4">
                                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${card.bg_class}`}>
                                            <IconComponent className={`h-6 w-6 ${card.color_class}`} />
                                        </div>
                                        <div>
                                            <span className="text-[10px] font-bold text-slate-400 block mb-0.5">ORDER #{card.order_index}</span>
                                            <h3 className="font-bold text-slate-800 text-lg">{card.title_en}</h3>
                                        </div>
                                    </div>
                                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${card.is_active ? 'bg-green-50 text-green-600' : 'bg-slate-100 text-slate-500'}`}>
                                        {card.is_active ? 'Active' : 'Inactive'}
                                    </span>
                                </div>
                                <div className="space-y-2 flex-grow">
                                    {points.slice(0, 3).map((point, i) => (
                                        <div key={i} className="flex gap-2 items-start text-sm text-slate-600">
                                            <div className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${card.bg_class.replace('bg-', 'bg-').replace('50', '400')}`} />
                                            <p className="line-clamp-2">{point}</p>
                                        </div>
                                    ))}
                                    {points.length > 3 && (
                                        <p className="text-xs text-slate-400 italic">+{points.length - 3} more points</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-3xl font-bold text-slate-800">Eligibility Cards</h1>
                    <p className="text-slate-500 mt-1">Manage static eligibility criteria cards for the Scorecard page.</p>
                </div>
                {!isAdding && !editingCard && cards.length > 0 && (
                    <button
                        onClick={() => setIsAdding(true)}
                        className="px-4 py-2 bg-[#283079] text-white rounded-lg hover:bg-[#1f2559] flex items-center gap-2 font-semibold shadow-md"
                    >
                        <PlusIcon className="h-5 w-5" />
                        Add New Card
                    </button>
                )}
            </div>

            {/* Section Settings */}
            {!isAdding && !editingCard && (
                <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
                    <h2 className="text-lg font-bold text-slate-800 mb-4 text-center">Section Header Settings</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Section Title
                            </label>
                            <input
                                type="text"
                                value={settings.section_title}
                                onChange={(e) => setSettings({ ...settings, section_title: e.target.value })}
                                className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#283079] focus:border-transparent"
                                placeholder="e.g., Eligibility Criteria"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Section Subtitle
                            </label>
                            <textarea
                                value={settings.section_subtitle}
                                onChange={(e) => setSettings({ ...settings, section_subtitle: e.target.value })}
                                className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#283079] focus:border-transparent h-[42px]"
                                placeholder="e.g., Find out if you're eligible..."
                                rows={1}
                            />
                        </div>
                    </div>
                    <div className="mt-4 flex justify-end">
                        <button
                            onClick={handleSettingsSave}
                            disabled={savingSettings}
                            className="bg-[#283079] hover:bg-[#1f2559] text-white px-6 py-2 rounded-xl font-semibold transition-all disabled:opacity-50 shadow-md"
                        >
                            {savingSettings ? 'Saving...' : 'Save Settings'}
                        </button>
                    </div>
                </div>
            )}

            {/* Card Form */}
            {(isAdding || editingCard) && (
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
                    <h3 className="text-xl font-bold mb-6 text-slate-800 border-b pb-4">{isAdding ? 'Add New Card' : 'Edit Card'}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                        <div className="lg:col-span-2">
                            <label className="block text-sm font-semibold text-slate-700 mb-1">Card Title</label>
                            <input
                                type="text"
                                value={formData.title_en}
                                onChange={(e) => setFormData({ ...formData, title_en: e.target.value })}
                                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#283079]"
                                placeholder="e.g. Salaried"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-1">Icon Name</label>
                            <select
                                value={formData.icon_name}
                                onChange={(e) => setFormData({ ...formData, icon_name: e.target.value })}
                                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#283079]"
                            >
                                {iconsList.map(icon => <option key={icon} value={icon}>{icon}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-1">Color Theme</label>
                            <select
                                value={formData.color_class}
                                onChange={(e) => {
                                    const selected = colorsList.find(c => c.color === e.target.value);
                                    setFormData({ ...formData, color_class: selected.color, bg_class: selected.bg });
                                }}
                                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#283079]"
                            >
                                {colorsList.map(c => <option key={c.color} value={c.color}>{c.name}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-1">Display Order</label>
                            <input
                                type="number"
                                value={formData.order_index}
                                onChange={(e) => setFormData({ ...formData, order_index: parseInt(e.target.value) || 0 })}
                                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#283079]"
                            />
                        </div>
                    </div>
                    
                    <div className="mb-6 flex items-center gap-3 bg-slate-50 p-4 rounded-xl">
                        <div className="flex items-center h-5">
                            <input
                                id="is_active"
                                type="checkbox"
                                checked={formData.is_active}
                                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                className="w-5 h-5 text-[#283079] border-slate-300 rounded focus:ring-[#283079]"
                            />
                        </div>
                        <div className="flex flex-col">
                            <label htmlFor="is_active" className="text-sm font-semibold text-slate-800">
                                Active Status
                            </label>
                            <p className="text-xs text-slate-500">
                                When active, this card will be visible on the public scorecard page.
                            </p>
                        </div>
                    </div>

                    <div className="border-t pt-4">
                        <div className="flex justify-between items-center mb-4">
                            <label className="block text-sm font-semibold text-slate-700">Card Points (Description List)</label>
                            <button type="button" onClick={handleAddPoint} className="text-xs bg-[#283079] text-white px-3 py-1.5 rounded-lg font-medium shadow-sm hover:bg-[#1f2559]">Add Point</button>
                        </div>
                        <div className="space-y-3">
                            {formData.points_en.map((point, index) => (
                                <div key={index} className="flex gap-2">
                                    <div className="mt-2 text-slate-400 font-medium">{index + 1}.</div>
                                    <textarea
                                        value={point}
                                        onChange={(e) => handlePointChange(index, e.target.value)}
                                        className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#283079]"
                                        placeholder="Enter description point"
                                        rows={2}
                                    />
                                    {formData.points_en.length > 1 && (
                                        <button type="button" onClick={() => handleRemovePoint(index)} className="text-red-500 p-2 hover:bg-red-50 rounded-lg h-fit"><Trash2 size={18} /></button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="flex gap-3 mt-8 justify-end">
                        <button
                            onClick={handleCancel}
                            className="px-6 py-2.5 bg-slate-100 text-slate-700 font-semibold rounded-xl hover:bg-slate-200 flex items-center gap-2"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSaveCard}
                            className="px-6 py-2.5 bg-[#283079] text-white font-semibold rounded-xl hover:bg-[#1f2559] flex items-center gap-2 shadow-md"
                        >
                            {isAdding ? 'Save Card' : 'Update Card'}
                        </button>
                    </div>
                </div>
            )}

            {/* Cards Grid */}
            {!isAdding && !editingCard && renderCardGrid()}

            <ConfirmDialog
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, cardId: null })}
                onConfirm={confirmDelete}
                title="Delete Eligibility Card"
                message="Are you sure you want to delete this card? This action cannot be undone."
            />
        </div>
    );
};

export default AdminScorecardEligibilityCardsPage;
